export const AVATAR_MAX_BYTES = 200 * 1024 * 1024;
export const AVATAR_MIN_DIMENSION = 100;
export const AVATAR_MAX_DIMENSION = 2000;

/** Допустимые пропорции: квадрат 1:1 и паспортные 3:4 / 4:3 (±8%). */
const ALLOWED_ASPECT_RATIOS = [1, 3 / 4, 4 / 3];
const ASPECT_TOLERANCE = 0.08;

export function isAllowedAvatarAspectRatio(width: number, height: number): boolean {
  if (width <= 0 || height <= 0) return false;
  const ratio = width / height;
  return ALLOWED_ASPECT_RATIOS.some(
    (target) => Math.abs(ratio - target) / target <= ASPECT_TOLERANCE,
  );
}

const loadImageDimensions = (file: File): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Не удалось прочитать изображение'));
    };
    img.src = url;
  });

/** Возвращает текст ошибки или null, если файл подходит для аватара. */
export async function validateAvatarFile(file: File): Promise<string | null> {
  if (file.size > AVATAR_MAX_BYTES) {
    return 'Размер файла не должен превышать 200 МБ';
  }

  let dimensions: { width: number; height: number };
  try {
    dimensions = await loadImageDimensions(file);
  } catch {
    return 'Не удалось прочитать изображение. Используйте JPG или PNG';
  }

  const { width, height } = dimensions;

  if (width < AVATAR_MIN_DIMENSION || height < AVATAR_MIN_DIMENSION) {
    return `Минимальный размер фото: ${AVATAR_MIN_DIMENSION}×${AVATAR_MIN_DIMENSION} пикселей`;
  }

  if (width > AVATAR_MAX_DIMENSION || height > AVATAR_MAX_DIMENSION) {
    return `Максимальный размер фото: ${AVATAR_MAX_DIMENSION}×${AVATAR_MAX_DIMENSION} пикселей`;
  }

  if (!isAllowedAvatarAspectRatio(width, height)) {
    return 'Фото должно быть квадратным (1:1) или паспортным (3:4). Сильно вытянутые снимки не принимаются';
  }

  return null;
}

/** Пропорции превью: 3:4 для портретных, 1:1 для квадратных. */
export function avatarPreviewSize(
  width: number,
  height: number,
  baseWidth = 250,
): { width: number; height: number } {
  const ratio = width / height;
  if (Math.abs(ratio - 1) / 1 <= ASPECT_TOLERANCE) {
    return { width: baseWidth, height: baseWidth };
  }
  if (ratio < 1) {
    return { width: baseWidth, height: Math.round((baseWidth * 4) / 3) };
  }
  return { width: Math.round((baseWidth * 4) / 3), height: baseWidth };
}
